"use client";

import { mapMetaEvent } from "@/lib/meta-route-policy";

export type AnalyticsEventName =
  | "page_view"
  | "cta_clicked"
  | "form_started"
  | "form_submitted"
  | "lead_submit_attempted"
  | "lead_submit_succeeded"
  | "lead_submit_failed"
  | "calendar_clicked"
  | "whatsapp_opened"
  | "outbound_link_clicked"
  | "scroll_depth_reached"
  | "engaged_session"
  | "activity_batch";

type AnalyticsProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

type AttributionInput = Partial<{
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  fbclid: string;
  gclid: string;
}>;

type MetaSnapshot = {
  metaSourceUrl: string;
  referrer: string | null;
};

type MetaDispatch = {
  name: string;
  standard: boolean;
  id: string;
  snapshot: MetaSnapshot;
};

type MetaBrowserContextValue = {
  freshAttribution: Record<string, string>;
  metaSourceUrl: string;
  referrer: string | null;
  metaDispatchAllowed: boolean;
};

type MetaWindow = Window & {
  saMetaSend?: (event: MetaDispatch) => void;
  saMetaPending?: MetaDispatch[];
  saMetaContext?: Partial<MetaBrowserContextValue>;
  saMetaRefreshContext?: () => unknown;
};

type MetaClickAcquisition = {
  fbclid: string;
  fbcTimestampMs: number;
  fbc: string;
};

type MetaClickState = {
  visitor_id: string;
  acquisitions: Record<string, MetaClickAcquisition>;
  gclid?: string;
};

const VISITOR_KEY = "staffable_visitor_id";
const SESSION_KEY = "staffable_session";
const UTM_KEY = "staffable_attribution";
const META_CLICK_KEY = "sa_meta_clicks_v2";
const SESSION_TTL = 30 * 60 * 1000;
const MAX_META_CLICK_ACQUISITIONS = 20;

const inMemoryMetaClickStates = new Map<string, MetaClickState>();

function id(prefix: string): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);

  return (
    prefix +
    "_" +
    Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")
  );
}

function getVisitorId(): string {
  try {
    const existing = localStorage.getItem(VISITOR_KEY);

    if (existing) return existing;

    const next = id("v");
    localStorage.setItem(VISITOR_KEY, next);
    return next;
  } catch {
    return id("v");
  }
}

function getSessionId(): string {
  const now = Date.now();

  try {
    const raw = sessionStorage.getItem(SESSION_KEY);

    if (raw) {
      const parsed = JSON.parse(raw) as { id?: string; touched?: number };

      if (parsed.id && parsed.touched && now - parsed.touched < SESSION_TTL) {
        sessionStorage.setItem(
          SESSION_KEY,
          JSON.stringify({ id: parsed.id, touched: now })
        );
        return parsed.id;
      }
    }

    const next = id("s");
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ id: next, touched: now })
    );
    return next;
  } catch {
    return id("s");
  }
}

function validTrackingValue(
  value: unknown,
  maximum = 1024
): string | undefined {
  return typeof value === "string" && value.length > 0 && value.length <= maximum
    ? value
    : undefined;
}

function utm(freshInput: AttributionInput = {}): Record<string, string> {
  let stored: Record<string, string> = {};

  try {
    const saved = JSON.parse(localStorage.getItem(UTM_KEY) || "{}") as unknown;

    if (saved && typeof saved === "object") {
      for (const key of [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "fbclid",
        "gclid",
      ] as const) {
        const value = validTrackingValue(
          (saved as Record<string, unknown>)[key]
        );

        if (value) stored[key] = value;
      }
    }
  } catch {
    // Existing attribution remains best effort.
  }

  const resolved = { ...stored };

  for (const key of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "fbclid",
    "gclid",
  ] as const) {
    const value = validTrackingValue(freshInput[key]);

    if (value) resolved[key] = value;
  }

  try {
    localStorage.setItem(UTM_KEY, JSON.stringify(resolved));
  } catch {
    // Attribution storage must not block analytics.
  }

  return resolved;
}

function readTrackingCookie(name: string): string | undefined {
  try {
    const prefix = `${encodeURIComponent(name)}=`;

    for (const value of document.cookie.split(";")) {
      const cookie = value.trim();

      if (cookie.startsWith(prefix)) {
        return decodeURIComponent(cookie.slice(prefix.length));
      }
    }
  } catch {
    // Cookie access is best effort.
  }

  return undefined;
}

function supportedOriginRootFallback(): MetaBrowserContextValue | null {
  try {
    const origin = new URL(window.location.origin);

    if (origin.protocol !== "https:" && origin.protocol !== "http:") {
      return null;
    }

    return {
      freshAttribution: {},
      metaSourceUrl: `${origin.origin}/`,
      referrer: null,
      metaDispatchAllowed: true,
    };
  } catch {
    return null;
  }
}

function normalizeMetaContext(
  candidate: unknown
): MetaBrowserContextValue | null {
  const fallback = supportedOriginRootFallback();

  if (!fallback) return null;

  try {
    const context = candidate as Partial<MetaBrowserContextValue> | undefined;

    const freshAttribution =
      context?.freshAttribution &&
      typeof context.freshAttribution === "object"
        ? context.freshAttribution
        : {};

    const source =
      typeof context?.metaSourceUrl === "string" &&
      context.metaSourceUrl.startsWith(fallback.metaSourceUrl) &&
      !context.metaSourceUrl.includes("?") &&
      !context.metaSourceUrl.includes("#")
        ? context.metaSourceUrl
        : fallback.metaSourceUrl;

    const referrer =
      typeof context?.referrer === "string" &&
      context.referrer.startsWith(fallback.metaSourceUrl) &&
      !context.referrer.includes("?") &&
      !context.referrer.includes("#")
        ? context.referrer
        : null;

    return {
      freshAttribution,
      metaSourceUrl: source,
      referrer,
      metaDispatchAllowed: true,
    };
  } catch {
    return fallback;
  }
}

function resolveCollectorMetaContext(): MetaBrowserContextValue | null {
  const fallback = supportedOriginRootFallback();

  if (!fallback) return null;

  try {
    const target = window as MetaWindow;
    const context =
      typeof target.saMetaRefreshContext === "function"
        ? target.saMetaRefreshContext()
        : target.saMetaContext;

    return normalizeMetaContext(context);
  } catch {
    return fallback;
  }
}

function snapshotMetaContext(
  context: MetaBrowserContextValue | null
): MetaSnapshot | null {
  if (!context || context.metaDispatchAllowed !== true) return null;

  return {
    metaSourceUrl: context.metaSourceUrl,
    referrer: context.referrer,
  };
}

function parseMatchingFbc(
  value: unknown,
  fbclid: string
): MetaClickAcquisition | undefined {
  const fbc = validTrackingValue(value, 2048);

  if (!fbc) return undefined;

  const match = /^fb\.1\.(\d{1,16})\.(.+)$/.exec(fbc);

  if (!match || match[2] !== fbclid) return undefined;

  const fbcTimestampMs = Number(match[1]);

  if (!Number.isSafeInteger(fbcTimestampMs) || fbcTimestampMs <= 0) {
    return undefined;
  }

  return { fbclid, fbcTimestampMs, fbc };
}

function validStoredAcquisition(
  value: unknown,
  fbclid: string
): MetaClickAcquisition | undefined {
  if (!value || typeof value !== "object") return undefined;

  const stored = value as Partial<MetaClickAcquisition>;

  const fbcTimestampMs = stored.fbcTimestampMs;
  if (
    stored.fbclid !== fbclid ||
    typeof fbcTimestampMs !== "number" ||
    !Number.isSafeInteger(fbcTimestampMs) ||
    fbcTimestampMs <= 0
  ) {
    return undefined;
  }

  return parseMatchingFbc(stored.fbc, fbclid);
}

function readMetaClickState(visitorId: string): MetaClickState {
  const memory = inMemoryMetaClickStates.get(visitorId);

  if (memory) return memory;

  try {
    const state = JSON.parse(
      localStorage.getItem(META_CLICK_KEY) || "null"
    ) as MetaClickState | null;

    if (
      state?.visitor_id === visitorId &&
      state.acquisitions &&
      typeof state.acquisitions === "object"
    ) {
      inMemoryMetaClickStates.set(visitorId, state);
      return state;
    }
  } catch {
    // Use the page-lifetime state below.
  }

  const state: MetaClickState = {
    visitor_id: visitorId,
    acquisitions: {},
  };

  inMemoryMetaClickStates.set(visitorId, state);
  return state;
}

function writeMetaClickState(state: MetaClickState): void {
  inMemoryMetaClickStates.set(state.visitor_id, state);

  try {
    localStorage.setItem(META_CLICK_KEY, JSON.stringify(state));
  } catch {
    // Memory state preserves this page's acquisition timestamp.
  }
}

function resolveMetaAttribution(
  visitorId: string,
  resolvedAttribution: Record<string, unknown>,
  freshAttribution: Record<string, string>
): Record<string, unknown> {
  const state = readMetaClickState(visitorId);
  const fbclid =
    validTrackingValue(freshAttribution.fbclid) ??
    validTrackingValue(resolvedAttribution.fbclid);
  const gclid =
    validTrackingValue(freshAttribution.gclid) ??
    validTrackingValue(resolvedAttribution.gclid) ??
    state.gclid;

  let fbc: string | undefined;

  if (fbclid) {
    const stored = validStoredAcquisition(state.acquisitions[fbclid], fbclid);
    const acquisition =
      stored ??
      parseMatchingFbc(resolvedAttribution.fbc, fbclid) ??
      parseMatchingFbc(readTrackingCookie("_fbc"), fbclid) ??
      (() => {
        const timestamp = Date.now();

        if (!Number.isSafeInteger(timestamp) || timestamp <= 0) {
          throw new Error("Safe acquisition timestamp unavailable");
        }

        return {
          fbclid,
          fbcTimestampMs: timestamp,
          fbc: `fb.1.${timestamp}.${fbclid}`,
        };
      })();

    state.acquisitions[fbclid] = acquisition;

    const records = Object.keys(state.acquisitions);

    if (records.length > MAX_META_CLICK_ACQUISITIONS) {
      records
        .filter((key) => key !== fbclid)
        .slice(0, records.length - MAX_META_CLICK_ACQUISITIONS)
        .forEach((key) => delete state.acquisitions[key]);
    }

    fbc = validStoredAcquisition(acquisition, fbclid)?.fbc;
  }

  if (gclid) state.gclid = gclid;
  writeMetaClickState(state);

  const { fbc: _discardedFbc, ...withoutFbc } = resolvedAttribution;

  return {
    ...withoutFbc,
    ...(fbclid ? { fbclid } : {}),
    ...(gclid ? { gclid } : {}),
    ...(fbc ? { fbc } : {}),
    ...(readTrackingCookie("_fbp") ? { fbp: readTrackingCookie("_fbp") } : {}),
  };
}

function newAnalyticsEventId(): string {
  if (typeof window.crypto?.randomUUID === "function") {
    return window.crypto.randomUUID();
  }

  throw new Error("Secure analytics event ID generator unavailable");
}

function fanOutToMeta(
  eventName: string,
  eventId: string,
  snapshot: MetaSnapshot | null
): void {
  try {
    if (!snapshot) return;

    const target = window as MetaWindow;
    const event: MetaDispatch = {
      ...mapMetaEvent(eventName),
      id: eventId,
      snapshot,
    };

    if (target.saMetaSend) {
      try {
        target.saMetaSend(event);
      } catch {
        // Meta failures cannot abort first-party analytics.
      }
      return;
    }

    const queue = (target.saMetaPending ||= []);

    if (queue.length < 100) queue.push(event);
  } catch {
    // Meta failures cannot abort first-party analytics.
  }
}

function endpoint(): string {
  return "/api/analytics";
}

export function trackEvent(
  eventName: AnalyticsEventName,
  properties: AnalyticsProperties = {}
): void {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();

    let collectionMetaContext: MetaBrowserContextValue | null = null;

    try {
      collectionMetaContext = resolveCollectorMetaContext();
    } catch {
      collectionMetaContext = supportedOriginRootFallback();
    }

    const metaSnapshot = snapshotMetaContext(collectionMetaContext);
    const freshAttribution = collectionMetaContext?.freshAttribution ?? {};
    const existingUtmAttribution = utm(freshAttribution);

    let metaAttribution: Record<string, unknown> = existingUtmAttribution;

    try {
      metaAttribution = resolveMetaAttribution(
        visitorId,
        existingUtmAttribution,
        freshAttribution
      );
    } catch {
      const { fbc: _discardedFbc, ...withoutFbc } = existingUtmAttribution;
      metaAttribution = withoutFbc;
    }

    const event_id = newAnalyticsEventId();
    const event_time = Math.floor(Date.now() / 1000);
    const {
      event_source_url: _discardedEventSourceUrl,
      event_referrer: _discardedEventReferrer,
      ...safeProperties
    } = properties;

    const body: Record<string, unknown> = {
      event_name: eventName,
      visitor_id: visitorId,
      session_id: sessionId,
      page_url: location.href,
      path: location.pathname,
      referrer: document.referrer,
      title: document.title,
      properties: safeProperties,
      ...metaAttribution,
      event_id,
      event_time,
    };

    delete body.event_source_url;
    delete body.event_referrer;

    const fallback = supportedOriginRootFallback();

    if (metaSnapshot) {
      body.event_source_url = metaSnapshot.metaSourceUrl;

      if (metaSnapshot.referrer) {
        body.event_referrer = metaSnapshot.referrer;
      }
    } else if (fallback) {
      body.event_source_url = fallback.metaSourceUrl;
    }

    try {
      fanOutToMeta(eventName, event_id, metaSnapshot);
    } catch {
      // Meta failures cannot abort first-party analytics.
    }

    const serialized = JSON.stringify(body);

    if (navigator.sendBeacon) {
      const sent = navigator.sendBeacon(
        endpoint(),
        new Blob([serialized], { type: "application/json" })
      );

      if (sent) return;
    }

    fetch(endpoint(), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: serialized,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Analytics must never affect the visitor's real action.
  }
}